import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import BackgroundWrapper from '../../components/wrapper'
import { MiniSigninHeader } from '../../components/layouts/navbar'
import { Footer } from '../../components/layouts/footer'
import useRequest from '../../components/hooks/use-req'
import { showToast } from '../../components/toast'
import Textarea from '../../components/textarea'
import Button from '../../components/button'
import { GreenThick } from '../../assets/icon'

interface RatingProductInfo {
    recommender_name: string;
    recommender_image_url?: string | null;
    product_title: string;
    product_description: string;
    product_image: string;
}
const RATING_LABELS = ['Not Helpful', 'Slightly Helpful', 'Somewhat Helpful', 'Helpful', 'Highly Helpful'];

const parsePreviewHtml = (html: string) => {
    if (!html) return { title: '', description: '', image: '' };

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const image = doc.querySelector('img')?.getAttribute('src') || '';
    const title = doc.querySelector('h3')?.textContent?.trim() || '';
    const description = doc.querySelector('p')?.textContent?.trim() || '';

    return { title, description, image };
};

const Star: React.FC<{ filled: boolean; size?: number }> = ({ filled, size = 32 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M12 2.5L14.9187 8.41443L21.4551 9.36901L16.7276 13.9755L17.8373 20.485L12 17.4144L6.16267 20.485L7.27235 13.9755L2.54486 9.36901L9.08133 8.41443L12 2.5Z"
            fill={filled ? '#00A86B' : 'none'}
            stroke={filled ? '#00A86B' : '#D1D5DB'}
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
    </svg>
);

interface StarRatingProps {
    value: number;
    onChange?: (value: number) => void;
    readOnly?: boolean;
    size?: number;
}

const StarRating: React.FC<StarRatingProps> = ({ value, onChange, readOnly = false, size }) => {
    const [hovered, setHovered] = useState(0);
    const displayValue = hovered || value;

    return (
        <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    disabled={readOnly}
                    onClick={() => onChange?.(star)}
                    onMouseEnter={() => !readOnly && setHovered(star)}
                    onMouseLeave={() => !readOnly && setHovered(0)}
                    className={readOnly ? 'cursor-default' : 'cursor-pointer'}
                >
                    <Star filled={star <= displayValue} size={size} />
                </button>
            ))}
        </div>
    );
};

const Rating = () => {
    const navigate = useNavigate();
    const { link_id: linkId } = useParams<{ link_id: string }>();
    const [searchParams] = useSearchParams();
    // Plain email address, no formatting needed — leftover "+<phone>" prefixing here was from
    // when this param carried a phone number, before the inter/page switched to collecting
    // email instead (see inter-screen.tsx).
    const email = searchParams.get('email') || '';

    const [info, setInfo] = useState<RatingProductInfo | null>(null);
    const [parsedMeta, setParsedMeta] = useState({ title: '', description: '', image: '' });
    const [rating, setRating] = useState(0);
    const [feedback, setFeedback] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { makeRequest: getRatingInfo } = useRequest(`/visitors/visitors/rate/${linkId}`, 'GET');

    const { makeRequest: submitRating, loading } = useRequest('/visitors/rate', 'POST');

    useEffect(() => {
        const fetchData = async () => {
            const [response] = await getRatingInfo(undefined, { email });
            setInfo(response);

            if (response?.preview_html) {
                setParsedMeta(parsePreviewHtml(response.preview_html));
            }
        };
        if (linkId) fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkId]);

    const recommenderName = info?.recommender_name || 'this recommender';
    const recommenderFirstName = recommenderName.split(' ')[0];
    const productTitle = parsedMeta.title || info?.product_title || 'No title available';
    const productDescription = parsedMeta.description || info?.product_description || 'No description available';
    const productImage = parsedMeta.image || info?.recommender_image_url || '';

    const handleSubmit = async () => {
        if (!rating) {
            showToast('Please select a star rating before submitting', false);
            return;
        }

        setIsSubmitting(true);
        try {
            const [response, error] = await submitRating({
                email,
                link_id: Number(linkId),
                score: rating,
                feedback: feedback || undefined,
            });

            if (error || response?.status === false) {
                showToast(error?.message || response?.message || 'Failed to submit rating', false);
                return;
            }

            showToast(response?.message || 'Rating submitted successfully', true);
            setSubmitted(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const ProductCard = () => (
        <div className="flex items-center gap-3 mb-6">
            <img
                src={info?.product_image || `https://ui-avatars.com/api/?name=${recommenderName}&background=EEE&color=333`}
                alt={recommenderName}
                className="w-9 h-9 rounded-full object-cover flex-shrink-0"
            />
            <div>
                <p className="text-[13px] text-[#65799A]">Recommended by</p>
                <p className="text-[15px] font-semibold text-[#121212]">{recommenderName}</p>
            </div>
        </div>
    );

    const benefits = [
        {
            title: 'Earn from Every Click',
            description: (
                <>
                    Get paid <span className="font-semibold">₦10</span> per valid click and{' '}
                    <span className="font-semibold">₦500</span> for every friend who joins during our launch offer
                </>
            ),
        },
        {
            title: 'Instant Vouch Links',
            description: 'Convert any product link into a trackable VouchLink in seconds',
        },
        {
            title: 'Share Everywhere',
            description: 'Share via WhatsApp, social media, or any platform with one click',
        },
    ];

    return (
        <BackgroundWrapper>
            <MiniSigninHeader />

            <div className="flex justify-center px-4 pb-16">
                <div className="w-full max-w-lg">
                    {submitted ? (
                        <>
                            <div className="text-center mb-6">
                                <h1 className="text-[22px] font-semibold text-gray-900 mb-1">
                                    Thank you for sharing your feedback!
                                </h1>
                                <p className="text-[14px] text-[#65799A]">
                                    Your rating has been submitted successfully.
                                </p>
                            </div>

                            <div className="bg-white rounded-2xl p-5 mb-4">
                                <ProductCard />

                                <div className="flex gap-3 mb-6">
                                    {productImage ? (
                                        <img
                                            src={productImage}
                                            alt={productTitle}
                                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                                        />
                                    ) : (
                                        <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <span className="text-gray-500 text-xs">No Image</span>
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{productTitle}</h3>
                                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{productDescription}</p>
                                    </div>
                                </div>

                                <p className="text-[13px] font-semibold text-[#65799A] mb-2">Your rating</p>
                                <StarRating value={rating} readOnly size={28} />
                                <p className="text-[12px] text-[#65799A] mt-2">{RATING_LABELS[rating - 1]}</p>
                            </div>

                            <div className="bg-white rounded-2xl p-5 mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <img src="https://res.cloudinary.com/djvwhtgeq/image/upload/v1760787442/Logo_opa4ss.svg" alt="VouchLinks" className="h-6" />
                                    <h2 className="text-[16px] font-bold text-[#121212]">
                                        Join VouchLinks and turn your recommendations into rewards
                                    </h2>
                                </div>
                                <p className="text-[13px] text-[#65799A] mb-4">
                                    If you love recommending products or services, join VouchLinks and start earning
                                    rewards whenever people engage with your recommendations. Join for free today!
                                </p>

                                <div className="space-y-3">
                                    {benefits.map((benefit) => (
                                        <div key={benefit.title} className="flex items-start gap-2.5">
                                            <span className="mt-0.5 flex-shrink-0">
                                                <GreenThick />
                                            </span>
                                            <div>
                                                <p className="text-[13px] font-semibold text-[#121212]">{benefit.title}</p>
                                                <p className="text-[12px] text-[#65799A] mt-0.5">{benefit.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <Button variant="primary" className="w-full" onClick={() => navigate('/signup')}>
                                Get Started
                            </Button>
                        </>
                    ) : (
                        <>
                            <div className="text-center mb-6">
                                <h1 className="text-[22px] font-semibold text-gray-900 mb-1">
                                    Please rate this recommendation from {recommenderFirstName}
                                </h1>
                                <p className="text-[14px] text-[#65799A]">
                                    Your rating helps the recommender earn a small reward and also helps us improve
                                    future recommendations
                                </p>
                            </div>

                            <div className="bg-white rounded-2xl p-5 mb-4">
                                <ProductCard />
                                <div className="flex gap-3">
                                    {productImage ? (
                                        <img
                                            src={productImage}
                                            alt={productTitle}
                                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                                        />
                                    ) : (
                                        <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <span className="text-gray-500 text-xs">No Image</span>
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{productTitle}</h3>
                                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{productDescription}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl p-5 mb-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                                        <rect x="2" y="4" width="20" height="14" rx="3" stroke="#00A86B" strokeWidth="1.5" />
                                        <path d="M8 20h8" stroke="#00A86B" strokeWidth="1.5" strokeLinecap="round" />
                                        <path d="M9 8.5l1.8 1.8L15 6.5" stroke="#00A86B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <h2 className="text-[16px] font-bold text-[#121212]">How would you rate this recommendation?</h2>
                                </div>
                                <p className="text-[13px] text-[#65799A] mb-5 ml-7">Tap a star to rate</p>

                                <div className="flex justify-between px-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setRating(star)}
                                            className="flex flex-col items-center gap-2"
                                        >
                                            <Star filled={star <= rating} size={32} />
                                            <span className="text-[10px] text-gray-500 text-center max-w-[60px]">
                                                {RATING_LABELS[star - 1]}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl p-5 mb-6">
                                <div className="flex items-center gap-2 mb-1">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                                        <path
                                            d="M12 2.5L14.9187 8.41443L21.4551 9.36901L16.7276 13.9755L17.8373 20.485L12 17.4144L6.16267 20.485L7.27235 13.9755L2.54486 9.36901L9.08133 8.41443L12 2.5Z"
                                            stroke="#00A86B"
                                            strokeWidth="1.5"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <h2 className="text-[16px] font-bold text-[#121212]">Any additional feedback (optional)</h2>
                                </div>
                                <p className="text-[13px] text-[#65799A] mb-4 ml-7">Help others by sharing more details</p>

                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-[14px] font-semibold text-[#121212]">Share your thoughts</span>
                                    <span className="text-xs text-gray-400">{feedback.length}/500</span>
                                </div>
                                <Textarea
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value.slice(0, 500))}
                                    placeholder="Share a few thoughts about this product or service"
                                    className="w-full"
                                />
                                <div className="flex justify-end mt-1">
                                    <button
                                        type="button"
                                        onClick={(e) => (e.target as HTMLButtonElement).blur()}
                                        className="text-[13px] underline text-[#333D4D] font-semibold"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>

                            <Button
                                variant="primary"
                                className="w-full"
                                onClick={handleSubmit}
                                disabled={isSubmitting || loading}
                            >
                                {isSubmitting || loading ? 'Submitting...' : 'Submit Rating'}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <Footer />
        </BackgroundWrapper>
    )
}

export default Rating
